import { useMemo } from "react";

import type { BinaryOpType, Expr, ExprBinary, ExprProduct, ExprSubExpr } from "../../../../data/live-charts/expression";
import { type CandleData, CandleDataProvider, useCandleData } from "../context";
import { useChartData } from "../hooks/useChartData";
import { ChartOverlay } from "../../ui/ChartOverlay";
import { useChartDataContext, useChartView } from "../../context";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@artis/logger";
import { useCallback, useEffect, useState } from "react";
import { sampleConfig } from "../../../../data/live-charts/config";
import { calculatePastTimeWithMarketGaps } from "../utils/time";
import { useStore } from "jotai";
import { utc } from "../../util";
import { isDebugMode } from "../../../../globals";

const reloadLimit: Record<string, number> = {};

const computedCandlesCache = new Map<string, CandleData[]>();
const chartSettingsCache = new Map<string, unknown>();
export const getComputedChartCandles = (chartId: string): CandleData[] | undefined => computedCandlesCache.get(chartId);
export const getComputedChartIds = (): string[] => Array.from(computedCandlesCache.keys());
export const getCapturedChartSettings = (chartId: string): unknown => chartSettingsCache.get(chartId);

export const ExpressionModifiers = ({ children }: { children: React.ReactNode }) => {
  const { chart } = useChartDataContext();
  const expr = chart?.expression;
  const inner = isDebugMode ? (
    <ComputedCandlesCapture chartId={chart?.id ?? ""} chart={chart}>
      {children}
    </ComputedCandlesCapture>
  ) : (
    children
  );
  if (!expr) return inner;
  return <ExpressionModifierEval expr={expr}>{inner}</ExpressionModifierEval>;
};

const ComputedCandlesCapture = ({
  chartId,
  chart,
  children,
}: {
  chartId: string;
  chart: unknown;
  children: React.ReactNode;
}) => {
  const candles = useCandleData();
  // Publish updates without churn — overwrites each tick, no delete-then-set race window.
  useEffect(() => {
    if (!chartId) return;
    computedCandlesCache.set(chartId, candles);
    chartSettingsCache.set(chartId, chart);
  }, [chartId, candles, chart]);
  // Clean up only when the chart unmounts or its id changes.
  useEffect(() => {
    if (!chartId) return;
    return () => {
      computedCandlesCache.delete(chartId);
      chartSettingsCache.delete(chartId);
    };
  }, [chartId]);
  return <>{children}</>;
};

const ExpressionModifierEval = ({ children, expr }: { children: React.ReactNode; expr: Expr }) => {
  if (expr.type !== "expr") return children;

  const { left, right, operator } = expr;

  if (left.type === "constant") {
    return (
      <ConstantExpressionModifier operator={operator} value={left.value} right={right}>
        {children}
      </ConstantExpressionModifier>
    );
  }

  if (left.type === "product") {
    return (
      <ProductExpressionModifier operator={operator} value={left} right={right}>
        {children}
      </ProductExpressionModifier>
    );
  }

  if (left.type === "subexpr") {
    return (
      <SubExpressionModifier operator={operator} value={left} right={right}>
        {children}
      </SubExpressionModifier>
    );
  }

  // Legacy: `left` is a nested ExprBinary. Treat it as an implicit sub-expression
  // so old data parses through the new evaluator without losing operands.
  const legacy = legacyChainToSubExpr(left);
  if (legacy) {
    return (
      <SubExpressionModifier operator={operator} value={legacy} right={right}>
        {children}
      </SubExpressionModifier>
    );
  }

  return children;
};

/** Promote a legacy nested chain in `left` slot into a self-contained sub-expression. */
const legacyChainToSubExpr = (node: ExprBinary): ExprSubExpr | undefined => {
  let baseCandidate: Expr = node.left;
  while (baseCandidate.type === "expr") baseCandidate = baseCandidate.left;
  if (baseCandidate.type !== "product" && baseCandidate.type !== "constant" && baseCandidate.type !== "subexpr") {
    return undefined;
  }
  // We can't safely reconstruct the original chain from arbitrary legacy nesting; treat the leftmost
  // leaf as the sub-expression base and the remaining right-recursive nodes as its chain.
  return { type: "subexpr", base: baseCandidate, chain: node.right?.type === "expr" ? node.right : undefined };
};

const ChainContinuation = ({ children, right }: { children: React.ReactNode; right?: Expr }) => {
  if (!right) return children;
  if (right.type !== "expr") return children;
  return <ExpressionModifierEval expr={right}>{children}</ExpressionModifierEval>;
};

const ConstantExpressionModifier = ({
  children,
  operator,
  value,
  right,
}: {
  children: React.ReactNode;
  operator: BinaryOpType;
  value: number;
  right?: Expr;
}) => {
  const data = useCandleData();
  const next = useMemo(() => {
    const result: CandleData[] = [];
    for (const x of data) {
      const close = safeEvalOp(operator, x.close, value);
      const open = safeEvalOp(operator, x.open, value);
      const high = safeEvalOp(operator, x.high, value);
      const low = safeEvalOp(operator, x.low, value);
      // Drop the candle entirely if any field is non-finite (e.g. `/ 0`), rendering a gap rather than an Infinity/NaN spike.
      if (close === undefined || open === undefined || high === undefined || low === undefined) continue;
      result.push({ ...x, close, open, high, low });
    }
    return result;
  }, [data, operator, value]);

  return (
    <CandleDataProvider value={next}>
      <ChainContinuation right={right}>{children}</ChainContinuation>
    </CandleDataProvider>
  );
};

const ProductExpressionModifier = ({
  children,
  operator,
  value,
  right,
}: {
  children: React.ReactNode;
  operator: BinaryOpType;
  value: ExprProduct;
  right?: Expr;
}) => {
  const client = useQueryClient();
  const { chart, curve_settings, loading, setReloading } = useChartDataContext();
  const parent = useCandleData();
  const filter = chart?.chartType === "candlestick" ? curve_settings?.filter_min_max : undefined;

  const data = useChartData({
    chart: {
      ...chart,
      editable: value.editable,
      artisType: value.artisType,
      productId: value.product.id,
      periodFrom: value.period.id,
    },
    enabled: true,
    filter,
  });

  const store = useStore();
  const config = sampleConfig[chart?.sampleTime ?? "10s"];
  const view = useChartView();

  const reload = useCallback(async () => {
    // We want to limit the number of reloads to 3 to prevent infinite reload loops in case of persistent errors. If the limit is exceeded, we log an error and do a full page reload as a fallback to ensure the user isn't stuck with a broken chart.
    reloadLimit[chart?.id ?? ""] = (reloadLimit[chart?.id ?? ""] ?? 0) + 1;
    if (reloadLimit[chart?.id ?? ""] >= 3) {
      logger.error(`ExpressionModifierEval: Reload limit exceeded.`, {
        chartId: chart?.id,
        expression: chart?.expression,
        reloadCount: reloadLimit[chart?.id ?? ""],
      });
      window.location.reload();
      return;
    }

    const candles = store.get(view.visible) + 30;
    const to = utc().valueOf();
    const from = calculatePastTimeWithMarketGaps(to, config.seconds, candles);

    const initialPageParam = {
      page: 0,
      from,
      to,
    };

    setReloading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    await client.setQueryData([chart], {
      pages: [{ data: [], ended: false }],
      pageParams: [initialPageParam],
    });
    await client.setQueryData(["sync"], null);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await client.invalidateQueries({ queryKey: [chart] });
    await client.invalidateQueries({ queryKey: ["sync"] });
  }, [client, chart, setReloading, store, view, config.seconds]);

  const candles = useMemo(() => {
    if (!data.candles) return [];
    const existing = new Map(parent.map((x) => [x.time, x.close]));
    const result: CandleData[] = [];
    let undefinedCount = 0;
    for (let i = 0; i < data.candles.length; i++) {
      const found = existing.get(data.candles?.[i]?.time);
      if (found === undefined) {
        undefinedCount++;
        continue;
      }

      const close = safeEvalOp(operator, found, data.candles[i].close);
      // Skip non-finite results (e.g. `/ 0`) without counting them as missing candles, so the gap-detection reload below only fires on genuinely absent data, not arithmetic gaps.
      if (close === undefined) continue;

      result.push({
        ...data.candles[i],
        close,
      });
    }

    // We want some tolerance for missing candles (i.e. usually the first or last may not match in time for brief periods). If more than 3 candles are missing, it's likely that there is a gap in the data, often when the user comes back to a stale tab, and in this case we want to do a full reload to prevent showing incorrect data. We check for existing.size > 0 to prevent this from happening on the initial load when we don't have any data for the other product yet.
    if (undefinedCount > 3 && existing.size > 0 && result.length !== parent.length) {
      logger.error(`ExpressionModifierEval: More than 3 candles had undefined parent value.`, {
        chartId: chart?.id,
        expression: chart?.expression,
        existingLength: existing.size,
        undefinedCount,
        parentLength: parent.length,
        dataLength: data.candles.length,
        operator,
      });
      reload();
    }

    return result;
  }, [data.candles, parent, operator, reload, chart?.id, chart?.expression]);

  return (
    <CandleDataProvider value={candles}>
      <ChartOverlay
        loading={data.historical.isLoading || loading}
        error={data.historical.error}
        empty={data.candles.length === 0}
      >
        <ChainContinuation right={right}>{children}</ChainContinuation>
      </ChartOverlay>
    </CandleDataProvider>
  );
};

const SubExpressionModifier = ({
  children,
  operator,
  value,
  right,
}: {
  children: React.ReactNode;
  operator: BinaryOpType;
  value: ExprSubExpr;
  right?: Expr;
}) => {
  const { chart } = useChartDataContext();
  const parent = useCandleData();
  const [subCandles, setSubCandles] = useState<CandleData[]>([]);

  const combined = useMemo(() => {
    if (subCandles.length === 0) return parent;
    const sub = new Map(subCandles.map((x) => [x.time, x.close]));
    const result: CandleData[] = [];
    let undefinedCount = 0;
    for (let i = 0; i < parent.length; i++) {
      const p = parent[i];
      const subClose = sub.get(p.time);
      if (subClose === undefined) {
        undefinedCount++;
        continue;
      }
      const close = safeEvalOp(operator, p.close, subClose);
      // Skip non-finite results (e.g. `/ 0`) without inflating the missing-candle count used by the gap warning below.
      if (close === undefined) continue;
      result.push({ ...p, close });
    }

    if (undefinedCount > 3 && sub.size > 0 && result.length !== parent.length) {
      logger.error(`SubExpressionModifier: More than 3 candles had undefined sub-expression value.`, {
        chartId: chart?.id,
        expression: chart?.expression,
        subLength: sub.size,
        undefinedCount,
        parentLength: parent.length,
        operator,
      });
    }

    return result;
  }, [parent, subCandles, operator, chart?.id, chart?.expression]);

  return (
    <>
      <SubExpressionBranch value={value} onCandles={setSubCandles} />
      <CandleDataProvider value={combined}>
        <ChainContinuation right={right}>{children}</ChainContinuation>
      </CandleDataProvider>
    </>
  );
};

const SubExpressionBranch = ({ value, onCandles }: { value: ExprSubExpr; onCandles: (c: CandleData[]) => void }) => {
  const [baseCandles, setBaseCandles] = useState<CandleData[]>([]);

  return (
    <>
      <SubBaseLoader base={value.base} onCandles={setBaseCandles} />
      <CandleDataProvider value={baseCandles}>
        {value.chain ? (
          <ExpressionModifierEval expr={value.chain}>
            <CandlesCapture onCandles={onCandles} />
          </ExpressionModifierEval>
        ) : (
          <CandlesCapture onCandles={onCandles} />
        )}
      </CandleDataProvider>
    </>
  );
};

const SubBaseLoader = ({
  base,
  onCandles,
}: {
  base: ExprProduct | { type: "constant"; value: number } | ExprSubExpr;
  onCandles: (c: CandleData[]) => void;
}) => {
  if (base.type === "product") return <BaseProductLoader product={base} onCandles={onCandles} />;
  if (base.type === "subexpr") return <SubExpressionBranch value={base} onCandles={onCandles} />;
  return <ConstantBaseLoader value={base.value} onCandles={onCandles} />;
};

const BaseProductLoader = ({ product, onCandles }: { product: ExprProduct; onCandles: (c: CandleData[]) => void }) => {
  const { chart, curve_settings } = useChartDataContext();
  const filter = chart?.chartType === "candlestick" ? curve_settings?.filter_min_max : undefined;

  const data = useChartData({
    chart: {
      ...chart,
      editable: product.editable,
      artisType: product.artisType,
      productId: product.product.id,
      periodFrom: product.period.id,
    },
    enabled: true,
    filter,
  });

  useEffect(() => {
    onCandles(data.candles);
  }, [data.candles, onCandles]);

  return null;
};

const ConstantBaseLoader = ({ value, onCandles }: { value: number; onCandles: (c: CandleData[]) => void }) => {
  const parent = useCandleData();
  useEffect(() => {
    // Synthesize a constant series aligned with the surrounding accumulator's timeline so the
    // sub-expression's chain operations (e.g. `(5 * B)`) line up with the rest of the chart.
    onCandles(parent.map((x) => ({ ...x, close: value, open: value, high: value, low: value })));
  }, [parent, value, onCandles]);
  return null;
};

const CandlesCapture = ({ onCandles }: { onCandles: (c: CandleData[]) => void }) => {
  const candles = useCandleData();
  useEffect(() => {
    onCandles(candles);
  }, [candles, onCandles]);
  return null;
};

const evalOp = (operator: BinaryOpType, a: number, b: number) => {
  switch (operator) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    case "/":
      return a / b;
    case "^":
      return a ** b;
    default:
      throw new Error(`Unknown operator: ${operator}`);
  }
};

/**
 * Guarded evaluation for candle values. Raw arithmetic can produce non-finite results — e.g.
 * `x / 0` → `±Infinity`, `0 / 0` → `NaN`, or `(-1) ** 0.5` → `NaN`. Feeding those to the chart
 * draws misleading spikes/gaps, so we treat any non-finite result as "no value" and let the
 * caller drop that candle (rendering an honest gap) instead of plotting garbage.
 */
const safeEvalOp = (operator: BinaryOpType, a: number, b: number): number | undefined => {
  const result = evalOp(operator, a, b);
  return Number.isFinite(result) ? result : undefined;
};
